<template>
  <div>
    <TopMenu
      :title="isEdit ? $t('Edit Schedule') : $t('Add Schedule')"
      :backOverwrite="displayStatus !== 'main' ? () => { displayStatus = 'main' } : () => { $router.push(ROUTE_PLAYLIST_SCHEDULE) }"
    />
    <div class="view">
      <DesktopBackLink :to="ROUTE_PLAYLIST_SCHEDULE" :label="$t('Playlist Schedule')" />
      <transition name="fade" appear>
        <div>
          <template v-if="displayStatus === 'daysOfWeek'">
            <MultiDaysOfWeekPicker
              :initValue="selectedDaysOfWeek"
              :onChange="daySelectChange"
              :onPickingDone="() => { displayStatus = 'main' }"
            />
          </template>
          <template v-if="displayStatus === 'main'">
            <div class="d-flex justify-content-between align-items-center p-1 mt-2">
              <div class="pl-4"><strong>{{$t('Days')}}</strong></div>
              <div class="col-8 pr-0">
                <a v-if="!isEdit" href="#" @click.prevent="displayStatus = 'daysOfWeek'">
                  <div class="bg-dark text-white px-3 py-2 d-block d-flex justify-content-start align-items-center rounded clickable">
                    <span>{{ selectedDaysOfWeek.size > 0 ? Array.from(selectedDaysOfWeek).sort().map(_i => daysOfWeek[_i].name).join(', ') : ' ' }}&nbsp;</span>
                  </div>
                </a>
                <div v-else class="select-wrapper">
                  <select class="custom-select bg-dark border-0 " v-model="day">
                    <option v-for="(day, index) in daysOfWeek" :key="day.name" :value="index">{{day.name}}</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center p-1 ">
              <div class="pl-4"><strong>{{$t('Time')}}</strong></div>
              <div class="col-8 pr-0 d-flex justify-content-between align-items-center">
                <div class="select-wrapper">
                  <select class="custom-select bg-dark border-0 " v-model="hour">
                    <option v-for="i in hours" :key="i" :value="i">{{i}}</option>
                  </select>
                </div>
                <span class="px-2">:</span>
                <div class="select-wrapper">
                  <select class="custom-select bg-dark border-0 " v-model="minute">
                    <option v-for="j in minutes" :key="j" :value="j">{{j}}</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center p-1 ">
              <div class="pl-4"><strong>{{$t('Playlist')}}</strong></div>
              <div class="col-8 pr-0">
                <a @click.prevent="pickPlaylist()" :href="pickPlaylistRoute">
                  <div class="bg-dark text-white px-3 py-2 d-block d-flex justify-content-start align-items-center rounded clickable">
                    <span>{{ selectedPlaylist != null ? selectedPlaylist.name + ' (' + selectedPlaylist.hoursAndSeconds + ')' : $t('Stop playback') }}</span>
                  </div>
                </a>
              </div>
            </div>
          </template>
          <div v-if="displayStatus === 'main'" class="px-1 mb-1 mt-2">
            <div @click="save()" class="bg-gray text-center clickable rounded pointer px-4 py-3">
              <span :class="canSave ? 'text-primary' : 'text-muted'">{{$t('Save')}}</span>
            </div>
          </div>
          <div v-if="isEdit" class="px-1 mb-1 mt-2">
            <div @click="deleteSchedule()" class="bg-gray text-center text-danger clickable rounded pointer px-4 py-3">
              <span>{{$t('Delete')}}</span>
            </div>
          </div>
        </div>
      </transition>
      <OverlayLoader :displayOverlay="savingSchedule || loadingSchedule || deletingSchedule" />
    </div>
  </div>
</template>

<script>
import days from '@/utils/days'
import musicboxClient from '@/utils/musicboxClient'
import { handle400 } from '@/utils/responseUtils'
import { ROUTE_PLAYLISTS, ROUTE_PLAYLIST_SCHEDULE } from '@/const/routeNames'
import DesktopBackLink from '@/components/shared/DesktopBackLink.vue'

export default {

  name: 'PlaylistScheduleAddEdit',

  components: {
    DesktopBackLink
  },

  created () {
    this.isEdit = this.$route.params.id != null
    if (this.$store.state.scheduleEdit.editedSchedule == null) {
      this.$store.commit('initEditedSchedule')
      if (this.$route.params.id != null) {
        this.loadingSchedule = true
        musicboxClient
          .getSchedule(this.$store.state.audio.currentBox.uuid, this.$route.params.id)
          .then(res => {
            this.$store.commit('updateEditedSchedule', { key: 'day', value: res.data.start_day })
            this.$store.commit('updateEditedSchedule', { key: 'hour', value: res.data.start_time.substr(0, 2) })
            this.$store.commit('updateEditedSchedule', { key: 'minute', value: res.data.start_time.substr(3, 2) })
            return res.data.playlist ? musicboxClient.getPlaylist(res.data.playlist.id) : { data: null }
          })
          .then(res => {
            this.$store.commit('updateEditedSchedule', { key: 'playlist', value: res.data })
          })
          .catch(e => {
            console.log('Error fetching schedule data', e)
            this.$notify.danger(this.$t('Failed fetching schedule data'))
          })
          .then(() => { this.loadingSchedule = false })
      }
    }
  },

  data () {
    const hours = []
    for (var i = 0; i < 24; i++) {
      hours.push((i < 10 ? '0' : '') + i + '')
    }
    const minutes = []
    for (var j = 0; j < 60; j += 5) {
      minutes.push((j < 10 ? '0' : '') + j + '')
    }
    return {
      ROUTE_PLAYLIST_SCHEDULE,
      displayStatus: 'main',
      loadingSchedule: false,
      savingSchedule: false,
      daysOfWeek: days,
      hours,
      minutes,
      isEdit: false,
      ROUTE_PLAYLISTS,
      deletingSchedule: false
    }
  },

  computed: {
    day: {
      get () { return this.$store.state.scheduleEdit.editedSchedule.day },
      set (value) { this.$store.commit('updateEditedSchedule', { key: 'day', value }) }
    },
    hour: {
      get () { return this.$store.state.scheduleEdit.editedSchedule.hour },
      set (value) { this.$store.commit('updateEditedSchedule', { key: 'hour', value }) }
    },
    minute: {
      get () { return this.$store.state.scheduleEdit.editedSchedule.minute },
      set (value) { this.$store.commit('updateEditedSchedule', { key: 'minute', value }) }
    },
    selectedDaysOfWeek: {
      get () { return this.$store.state.scheduleEdit.editedSchedule.selectedDaysOfWeek },
      set (value) { this.$store.commit('updateEditedSchedule', { key: 'selectedDaysOfWeek', value }) }
    },
    selectedPlaylist () {
      return this.$store.state.scheduleEdit.editedSchedule.playlist
    },
    canSave () {
      if (this.isEdit) { return true }
      return this.selectedDaysOfWeek.size > 0
    },
    pickPlaylistRoute () {
      if (this.isEdit) {
        return ROUTE_PLAYLISTS + '?schedule=pick&scheduleId=' + this.$route.params.id
      }
      return ROUTE_PLAYLISTS + '?schedule=pick'
    }
  },

  methods: {
    daySelectChange (selectedDays) {
      this.selectedDaysOfWeek = selectedDays
    },
    save () {
      if (!this.canSave || this.savingSchedule) { return }
      this.savingSchedule = true

      if (this.isEdit) {
        const data = {
          playlist_id: this.selectedPlaylist ? this.selectedPlaylist.id : null,
          start_day: this.day,
          start_time: this.hour + ':' + this.minute
        }
        musicboxClient
          .updateSchedule(this.$store.state.audio.currentBox.uuid, this.$route.params.id, data)
          .then(res => {
            this.$store.commit('clearEditedSchedule')
            this.$notify.success(this.$t('Schedule updated'))
            this.$router.push(ROUTE_PLAYLIST_SCHEDULE)
          })
          .catch(e => {
            if (e.response && e.response.status === 400) { handle400(e.response.data, this) }
            this.$notify.danger(this.$t('Failed updating schedule'))
          })
          .then(() => { this.savingSchedule = false })
      } else {
        const requests = []
        this.selectedDaysOfWeek.forEach(day => requests.push({
          playlist_id: this.selectedPlaylist ? this.selectedPlaylist.id : null,
          start_day: day,
          start_time: this.hour + ':' + this.minute
        }))

        var savingSchedulesCount = requests.length
        var errors = 0

        requests.forEach(req => {
          musicboxClient
            .createSchedule(this.$store.state.audio.currentBox.uuid, req)
            .then(res => {})
            .catch(e => {
              errors++
              if (e.response && e.response.status === 400) { handle400(e.response.data, this) }
            })
            .then(() => {
              savingSchedulesCount--
              if (savingSchedulesCount === 0) {
                this.savingSchedule = false
                if (errors === 0) {
                  this.$notify.success(this.$t('Schedule created'))
                  this.$router.push(ROUTE_PLAYLIST_SCHEDULE)
                } else {
                  this.$notify.danger(this.$t('Failed creating schedule'))
                }
              }
            })
        })
      }
    },
    deleteSchedule () {
      this.deletingSchedule = true
      musicboxClient
        .deleteSchedule(this.$store.state.audio.currentBox.uuid, this.$route.params.id)
        .then(res => {
          this.$notify.success(this.$t('Schedule deleted'))
        })
        .catch(e => { this.$notify.danger(this.$t('Failed to delete schedule')) })
        .then(() => {
          this.deletingSchedule = false
          this.$router.push(ROUTE_PLAYLIST_SCHEDULE)
        })
    },
    pickPlaylist () {
      this.$router.push(this.pickPlaylistRoute)
    }
  }
}
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/PlaylistScheduleAddEdit.vue